import { Button, Typography } from '@breetometer/components';
import { CopyIcon, DownloadIcon, InfoIcon } from 'BreetAssets';
import { Checkbox } from 'BreetComponents';
import { copyText, viewHelpArticle } from 'BreetHelpers';
import { selectUserData, useAppSelector } from 'BreetRedux';
import { type ChangeEvent, useCallback, useState } from 'react';

import { type IAuthenticatorBackupCodeProps } from '../Dialog2faSettings.helpers';

const AuthenticatorBackupCode = ({ onNext, recoveryCodes }: IAuthenticatorBackupCodeProps) => {
	const user = useAppSelector(selectUserData);

	const [hasInteracted, setHasInteracted] = useState<boolean>(false);
	const [isBackupCodeSaved, setIsBackupCodeSaved] = useState<boolean>(false);
	const [isBackupCodeConfirmedSaved, setIsBackupCodeConfirmedSaved] = useState<boolean>(false);

	const downloadRecoveryCodes = (codes: string[], meta: { email?: string }) => () => {
		const header = [
			'Breet Recovery Codes',
			meta.email ? `Account: ${meta.email}` : undefined,
			`Generated: ${new Date().toISOString()}`,
			'',
			'Each code can be used once. Store this file securely.',
			'',
		]
			.filter(Boolean)
			.join('\n');

		const body = `${header}\n${codes.join('\n')}\n`;
		const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `breet-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
		document.body.appendChild(a);
		a.click();
		a.remove();
		URL.revokeObjectURL(url);

		setIsBackupCodeSaved(true);
	};

	const copyAllRecoveryCodes = (codes: string[]) => () => {
		const text = codes.join('\n');
		copyText(text, 'Recovery codes copied to clipboard').catch(console.error);

		setIsBackupCodeSaved(true);
	};

	const handleCheckboxAttestation = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setIsBackupCodeConfirmedSaved(e.target.checked);
		setHasInteracted(true);
	}, []);

	const isCompleteSetup = isBackupCodeSaved && isBackupCodeConfirmedSaved;

	return (
		<div className='authenticator-backup-code'>
			<Typography
				as='h2'
				className='color-neutral-50'
			>
				Save your codes
			</Typography>

			<div className='backup-code-info border-dark-navy bg-primary-400'>
				<InfoIcon className='svg-dark' />
				<Typography className='color-primary-50'>
					These 10 recovery codes let you sign in if you lose access to your 2FA method. Each one works only once, so store them
					securely.
				</Typography>
			</div>

			<div className='backup-codes border-dark-navy bg-primary-400'>
				{recoveryCodes.map((code) => (
					<div
						key={code}
						className='backup-codes-item'
					>
						<Typography className='border-dark-navy bg-primary-500 color-tertiary-400'>{code}</Typography>
					</div>
				))}
			</div>

			<div className='qr-share'>
				<Button
					className='download bg-primary-400 border-dark-navy color-neutral-50'
					onClick={downloadRecoveryCodes(recoveryCodes, { email: user?.email })}
					leadingIcon={
						<DownloadIcon
							variant='plain'
							className='svg-dark'
						/>
					}
				>
					Download
				</Button>
				<Button
					className='copy bg-primary-400 border-dark-navy color-neutral-50'
					leadingIcon={<CopyIcon variant='colored' />}
					onClick={copyAllRecoveryCodes(recoveryCodes)}
				>
					Copy
				</Button>
			</div>

			<div className='backup-code-attestation'>
				<Checkbox
					checked={isBackupCodeConfirmedSaved}
					onChange={handleCheckboxAttestation}
					errorMsg={hasInteracted && !isCompleteSetup ? "Please check the box first to confirm you've saved your codes." : ''}
				>
					<Typography
						as='p'
						variant='sub_head'
						className='color-tertiary-400'
					>
						I have saved these codes in a safe place.
					</Typography>
				</Checkbox>
			</div>

			<div className='qr-cta'>
				<div className='qr-learn-more'>
					<InfoIcon className='svg-dark' />
					<Typography
						as='a'
						onClick={viewHelpArticle('15391910')}
						className='color-tertiary-400'
					>
						Learn More
					</Typography>
				</div>

				<Button
					onClick={onNext}
					disabled={!isCompleteSetup}
				>
					Complete Setup
				</Button>
			</div>
		</div>
	);
};

export default AuthenticatorBackupCode;
